/**
 * Regions and shading rules of a Norinori grid: the TypeScript port of
 * `backend/apps/puzzletypes/norinori/shading.py`, kept at parity by the shared fixtures, plus
 * the live contradictions, which only the client computes. Pure functions over row strings.
 */
import { DOT, PER_REGION, SHADED, type Cell } from "./types";

export function key([r, c]: Cell): string {
  return `${r},${c}`;
}

export function neighbours(r: number, c: number, rows: number, cols: number): Cell[] {
  const out: Cell[] = [];
  for (const [dr, dc] of [
    [-1, 0],
    [1, 0],
    [0, -1],
    [0, 1],
  ]) {
    const nr = r + dr;
    const nc = c + dc;
    if (nr >= 0 && nr < rows && nc >= 0 && nc < cols) out.push([nr, nc]);
  }
  return out;
}

export function gridFits(grid: string[], rows: number, cols: number): boolean {
  return grid.length === rows && grid.every((line) => line.length === cols);
}

/** The region index of every cell, regions numbered in reading order of their first cell. */
export function regionMap(regions: string[]): number[][] {
  const rows = regions.length;
  const cols = regions[0]?.length ?? 0;
  const index = Array.from({ length: rows }, () => Array<number>(cols).fill(-1));
  let count = 0;
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < cols; c += 1) {
      if (index[r][c] !== -1) continue;
      const char = regions[r][c];
      index[r][c] = count;
      const stack: Cell[] = [[r, c]];
      while (stack.length > 0) {
        const [cr, cc] = stack.pop() as Cell;
        for (const [nr, nc] of neighbours(cr, cc, rows, cols)) {
          if (index[nr][nc] === -1 && regions[nr]?.[nc] === char) {
            index[nr][nc] = count;
            stack.push([nr, nc]);
          }
        }
      }
      count += 1;
    }
  }
  return index;
}

/** Every region's cells in reading order; a region's first cell is its anchor. */
export function regionCells(regions: string[]): Cell[][] {
  const groups: Cell[][] = [];
  regionMap(regions).forEach((line, r) =>
    line.forEach((k, c) => {
      (groups[k] ??= []).push([r, c]);
    }),
  );
  return groups;
}

export function cellsWith(grid: string[], char: string): Set<string> {
  const found = new Set<string>();
  grid.forEach((line, r) => {
    for (let c = 0; c < line.length; c += 1) if (line[c] === char) found.add(`${r},${c}`);
  });
  return found;
}

export type Issue = "region_count" | "not_domino";

/** Every rule a complete shading breaks, in the server's order. Empty when solved. */
export function shadingIssues(regions: string[], shading: string[]): Issue[] {
  const rows = regions.length;
  const cols = regions[0]?.length ?? 0;
  const shaded = cellsWith(shading, SHADED);
  const issues: Issue[] = [];
  if (
    regionCells(regions).some(
      (cells) => cells.filter((cell) => shaded.has(key(cell))).length !== PER_REGION,
    )
  )
    issues.push("region_count");
  // Every shaded cell has exactly one shaded neighbour; a domino may straddle two regions.
  const lonelyOrCrowded = [...shaded].some((k) => {
    const [r, c] = k.split(",").map(Number);
    return neighbours(r, c, rows, cols).filter((n) => shaded.has(key(n))).length !== 1;
  });
  if (lonelyOrCrowded) issues.push("not_domino");
  return issues;
}

export interface Contradictions {
  /** Anchors of the regions with more than two shaded cells, or fewer than two open ones. */
  regions: Cell[];
  /** Shaded cells with two shaded neighbours or more, or with no way left to a partner. */
  cells: Cell[];
}

const byReading = (a: Cell, b: Cell) => a[0] - b[0] || a[1] - b[1];

/**
 * What a partial grid already gets wrong, whatever is played next. Sound: it says nothing it
 * is not sure of, and it never reads the answer key (CLAUDE.md §10).
 */
export function contradictions(regions: string[], progress: string[]): Contradictions {
  const rows = regions.length;
  const cols = regions[0]?.length ?? 0;
  const shaded = cellsWith(progress, SHADED);
  const dotted = cellsWith(progress, DOT);
  const flagged: Cell[] = [];
  for (const cells of regionCells(regions)) {
    const mine = cells.filter((cell) => shaded.has(key(cell))).length;
    const open = cells.filter((cell) => !dotted.has(key(cell))).length;
    if (mine > PER_REGION || open < PER_REGION) flagged.push(cells[0]);
  }
  const bad: Cell[] = [];
  for (const k of shaded) {
    const [r, c] = k.split(",").map(Number);
    const around = neighbours(r, c, rows, cols);
    const partners = around.filter((n) => shaded.has(key(n))).length;
    const free = around.filter((n) => !shaded.has(key(n)) && !dotted.has(key(n))).length;
    if (partners >= 2 || (partners === 0 && free === 0)) bad.push([r, c]);
  }
  return { regions: flagged.sort(byReading), cells: bad.sort(byReading) };
}
